import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { walletApi } from '../lib/api';
import { formatCurrency, formatRelativeTime, getTransactionStyle } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Search, Filter, ChevronRight } from 'lucide-react';

const TransactionsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    try {
      const res = await walletApi.getTransactions({ limit: 100 });
      setTransactions(res.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTxs = transactions.filter(tx => {
    if (filter === 'in' && tx.direction !== 'in') return false;
    if (filter === 'out' && tx.direction !== 'out') return false;
    if (searchQuery && !tx.counterparty_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const groupByDate = (txs) => {
    const groups = {};
    txs.forEach(tx => {
      const date = new Date(tx.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      if (!groups[date]) groups[date] = [];
      groups[date].push(tx);
    });
    return groups;
  };

  const grouped = groupByDate(filteredTxs);

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8">
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 border-b border-white/10 lg:border-0 lg:bg-transparent lg:backdrop-blur-none lg:static">
          <div className="p-6 lg:p-0 lg:mb-6">
            <div className="flex items-center gap-4 mb-4 lg:mb-2">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
              <h1 className="text-xl lg:text-3xl font-bold flex-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Transactions</h1>
            </div>

            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transactions..."
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-0 focus:outline-none text-sm"
                data-testid="search-transactions"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'in', label: 'Received' },
                { key: 'out', label: 'Sent' },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filter === f.key ? 'bg-primary text-white' : 'bg-white/5 text-muted-foreground hover:text-white'}`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Transaction List */}
        <div className="px-6 lg:px-0 space-y-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
            </div>
          ) : filteredTxs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No transactions found</p>
            </div>
          ) : (
            Object.entries(grouped).map(([date, txs]) => (
              <div key={date}>
                <p className="text-xs text-muted-foreground font-medium mb-3 uppercase tracking-wider">{date}</p>
                <div className="bg-card border border-white/10 rounded-xl overflow-hidden divide-y divide-white/10">
                  {txs.map((tx) => {
                    const style = getTransactionStyle(tx.type, tx.direction);
                    const Icon = tx.direction === 'in' ? ArrowDownLeft : ArrowUpRight;
                    return (
                      <Link
                        key={tx.transaction_id}
                        to={`/transactions/${tx.transaction_id}`}
                        className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors"
                        data-testid={`tx-${tx.transaction_id}`}
                      >
                        <div className={`w-10 h-10 rounded-full ${style.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${style.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{tx.counterparty_name}</p>
                          <p className="text-sm text-muted-foreground">{tx.source_type.replace('_', ' ')} &middot; {formatRelativeTime(tx.created_at)}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`font-medium ${tx.direction === 'in' ? 'text-green-500' : ''}`}>
                            {tx.direction === 'in' ? '+' : '-'}{formatCurrency(tx.amount)}
                          </p>
                          <p className={`text-xs ${tx.status === 'completed' ? 'text-green-500' : tx.status === 'failed' ? 'text-red-500' : 'text-yellow-500'}`}>
                            {tx.status}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default TransactionsPage;
